import type { ApiOverview } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ApiEndpointStatusBadge } from '@/components/api-access/ApiEndpointStatusBadge'
import { LibraryTable, type LibraryColumn } from '@/components/LibraryTable'
import { PageHeader } from '@/components/PageHeader'
import { apiOverviewQuery } from '@/lib/api-access-queries'
import { topicOwnerLabel } from '@/lib/topic-overview'

/**
 * API & MCP across every account (Clipbook Internal): which workspaces have the product, the state
 * of each one's endpoint, and a click into the workspace's own API page — where the endpoint is set
 * up and its keys are managed, as every product is worked on in its workspace. Behind the same gate
 * as `/api/api-access` (`manage-all:users` in Clipbook's organization), so a refused visitor sees
 * the gate's own sentence.
 */
export const Route = createFileRoute('/app/api/')({
  loader: ({ context }) => context.queryClient.ensureQueryData(apiOverviewQuery),
  component: ApiOverviewPage,
})

type Workspace = ApiOverview['workspaces'][number]
type Summary = ApiOverview['unplaced'][number]

/** The workspace's API page: the account's own space at the account's URL, a client at its own. */
function WorkspaceLink({ workspace }: { workspace: Workspace }) {
  const className = 'font-medium text-foreground underline-offset-4 hover:underline'
  const label = topicOwnerLabel(workspace)
  return workspace.isPrimary ? (
    <Link
      to="/app/accounts/$accountId/api"
      params={{ accountId: workspace.accountId }}
      className={className}
    >
      {label}
    </Link>
  ) : (
    <Link
      to="/app/accounts/$accountId/subaccounts/$subaccountId/api"
      params={{ accountId: workspace.accountId, subaccountId: workspace.subaccountId }}
      className={className}
    >
      {label}
    </Link>
  )
}

const keysOf = (endpoint: Summary) => `${endpoint.activeKeys} of ${endpoint.keys}`

const COLUMNS: Array<LibraryColumn<Workspace>> = [
  { key: 'workspace', header: 'Workspace', cell: (row) => <WorkspaceLink workspace={row} /> },
  {
    key: 'endpoint',
    header: 'Endpoint',
    cell: (row) =>
      row.endpoint ? (
        row.endpoint.name
      ) : (
        <span className="text-muted-foreground">Not set up yet</span>
      ),
  },
  {
    key: 'status',
    header: 'Status',
    cell: (row) => row.endpoint && <ApiEndpointStatusBadge status={row.endpoint.status} />,
  },
  {
    key: 'product',
    header: 'API & MCP',
    cell: (row) =>
      row.entitled ? (
        'On'
      ) : (
        // The workspace's page is not offered until the account has the product.
        <span className="text-warning">Off for this account</span>
      ),
  },
  {
    key: 'topics',
    header: 'Topics',
    headClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
    cell: (row) => row.endpoint?.topicCount ?? '—',
  },
  {
    key: 'keys',
    header: 'Active keys',
    headClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
    cell: (row) => (row.endpoint ? keysOf(row.endpoint) : '—'),
  },
]

/** Endpoints made before endpoints were made in a workspace that migration 0070 could not place. */
function Unplaced({ endpoints }: { endpoints: Summary[] }) {
  if (endpoints.length === 0) return null
  return (
    <section aria-label="Not in a workspace" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-foreground">Not in a workspace</h2>
      <p className="text-sm text-muted-foreground">
        Their topics span several accounts, so they could not be placed in one. They keep working;
        to manage one, set its endpoint up again in the workspace it serves.
      </p>
      <ul className="flex flex-col divide-y divide-border rounded-md border border-border text-sm">
        {endpoints.map((endpoint) => (
          <li key={endpoint.id} className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="font-medium text-foreground">{endpoint.name}</span>
            <span className="flex items-center gap-3 text-muted-foreground tabular-nums">
              {endpoint.topicCount} topics · {keysOf(endpoint)} keys
              <ApiEndpointStatusBadge status={endpoint.status} />
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ApiOverviewPage() {
  const { data } = useSuspenseQuery(apiOverviewQuery)
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="API & MCP"
        description="Every workspace with API & MCP: its endpoint and keys. Open one to set up its endpoint or manage its keys."
        className="mb-0"
      />
      <LibraryTable
        rows={data.workspaces}
        columns={COLUMNS}
        rowKey={(row) => row.subaccountId}
        searchText={(row) => `${row.accountName} ${row.subaccountName} ${row.endpoint?.name ?? ''}`}
        searchPlaceholder="Search accounts…"
        emptyAll="No account has API & MCP yet. Turn it on in an account’s Products."
        emptyFiltered="No workspace matches."
      />
      <Unplaced endpoints={data.unplaced} />
    </div>
  )
}
